import { getClubTier, getEffectiveClub, type ClubTier } from './clubs'
import type { DecisionChoice, DecisionEffect, DecisionStatsSnapshot, LeagueMovement } from './types'

export type EventRiskBand = 'low' | 'high'

export interface FootballEvent {
  id: string
  title: string
  description: string
  icon: string
  riskBand: EventRiskBand
  choices: DecisionChoice[]
  stages?: readonly number[]
  clubTiers?: readonly ClubTier[]
}

export interface FootballEventContext {
  stage: number
  clubName: string
  teamName: string
  clubTier: ClubTier
  country: string
}

export const EVENTS: readonly FootballEvent[] = [
  { id: 'training', title: 'Entrenamiento voluntario', description: 'El entrenador ofrece una sesión extra después del entrenamiento.', icon: 'mdi:dumbbell', riskBand: 'low', choices: [
    { id: 'training-extra', title: 'Quedarte a entrenar', summary: 'Acelera tu desarrollo aunque las piernas lo notarán.', art: 'event-training-extra', badge: 'Entrenamiento', risk: 'medium', effects: [{ metric: 'physical', label: 'Físico', value: '−16', tone: 'cost' }, { metric: 'rating', label: 'OVR', value: '+1', tone: 'gain' }] },
    { id: 'training-rest', title: 'Cuidar el cuerpo', summary: 'Recuperas cuerpo y cabeza, aunque el cuerpo técnico esperaba más ambición.', art: 'event-training-rest', badge: 'Recuperación', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+10', tone: 'gain' }, { metric: 'morale', label: 'Moral', value: '+4', tone: 'gain' }, { metric: 'reputation', label: 'Prestigio', value: '−4', tone: 'cost' }] },
  ]},
  { id: 'coach', title: 'Conversación con el entrenador', description: 'Quiere saber si estás listo para asumir más responsabilidad.', icon: 'mdi:account-voice', riskBand: 'high', choices: [
    { id: 'coach-demand', title: 'Pedir protagonismo', summary: 'Pones tu ambición sobre la mesa y esperas su respuesta.', art: 'event-coach-demand', badge: 'Despacho', risk: 'high', effects: [{ metric: 'condition', label: 'Respuesta positiva', value: '50%', tone: 'gain', when: '+1 OVR y +12 moral' }, { metric: 'condition', label: 'Respuesta negativa', value: '50%', tone: 'cost', when: '−10 moral' }] },
    { id: 'coach-accept', title: 'Aceptar tu rol', summary: 'La claridad te tranquiliza, pero debes demostrar compromiso en el trabajo táctico.', art: 'event-coach-accept', badge: 'Vestuario', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '−4', tone: 'cost' }, { metric: 'morale', label: 'Moral', value: '+8', tone: 'gain' }] },
  ]},
  { id: 'injury', title: 'Molestias musculares', description: 'Notas una sobrecarga antes del próximo encuentro.', icon: 'mdi:bandage', riskBand: 'high', choices: [
    { id: 'injury-stop', title: 'Parar a tiempo', summary: 'Proteges el músculo, pero pierdes terreno en la jerarquía.', art: 'event-injury-stop', badge: 'Enfermería', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+12', tone: 'gain' }, { metric: 'reputation', label: 'Prestigio', value: '−5', tone: 'cost' }] },
    { id: 'injury-push', title: 'Entrenar igualmente', summary: 'Asumes el riesgo de convertir una molestia en lesión.', art: 'event-injury-push', badge: 'Entrenamiento', risk: 'high', effects: [{ metric: 'condition', label: 'Superas la sesión', value: '60%', tone: 'gain', when: '+1 OVR' }, { metric: 'condition', label: 'La carga empeora', value: '40%', tone: 'cost', when: '−28 físico' }] },
  ]},
  { id: 'press', title: 'Foco de la prensa', description: 'Una entrevista puede acercarte a la afición o distraerte del fútbol.', icon: 'mdi:microphone', riskBand: 'low', stages: [2, 3, 4], choices: [
    { id: 'press-speak', title: 'Dar la entrevista', summary: 'Aceptas una conversación directa, sin frases preparadas.', art: 'event-press-speak', badge: 'Prensa', risk: 'medium', effects: [{ metric: 'morale', label: 'Moral', value: '+10', tone: 'gain' }, { metric: 'physical', label: 'Físico', value: '−5', tone: 'cost' }] },
    { id: 'press-focus', title: 'Centrarte en el campo', summary: 'Cedes el protagonismo y completas una sesión táctica ligera.', art: 'event-press-focus', badge: 'Entrenamiento', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '−3', tone: 'cost' }, { metric: 'morale', label: 'Moral', value: '+6', tone: 'gain' }] },
  ]},
  { id: 'academy', title: 'Visita a la academia', description: 'Los juveniles quieren compartir una sesión contigo.', icon: 'mdi:school-outline', riskBand: 'low', stages: [3, 4], choices: [
    { id: 'academy-mentor', title: 'Ejercer de mentor', summary: 'Compartes lectura de juego y ganas perspectiva a costa de energía.', art: 'event-academy-mentor', badge: 'Cantera', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '−6', tone: 'cost' }, { metric: 'morale', label: 'Moral', value: '+14', tone: 'gain' }] },
    { id: 'academy-drill', title: 'Competir con ellos', summary: 'Conviertes la visita en un entrenamiento intenso.', art: 'event-academy-drill', badge: 'Cantera', risk: 'medium', effects: [{ metric: 'rating', label: 'OVR', value: '+1', tone: 'gain' }, { metric: 'physical', label: 'Físico', value: '−8', tone: 'cost' }] },
  ]},
  { id: 'conditioning', title: 'Datos del preparador', description: 'La sesión puede adaptarse a las herramientas que ya forman parte de tu build.', icon: 'mdi:chart-timeline-variant', riskBand: 'low', choices: [
    { id: 'conditioning-load', title: 'Completar la carga', summary: 'Aceptas una sesión exigente para acelerar el desarrollo.', art: 'event-conditioning-load', badge: 'Resistencia', risk: 'medium', effects: [{ metric: 'physical', label: 'Físico', value: '−10', tone: 'cost' }, { metric: 'rating', label: 'OVR', value: '+1', tone: 'gain' }] },
    { id: 'conditioning-biometric', title: 'Optimizar con tus datos', summary: 'La Pulsera biométrica ajusta cada serie y evita trabajo inútil.', art: 'event-conditioning-biometric', badge: 'Reliquia requerida · Pulsera biométrica', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '−6', tone: 'cost' }, { metric: 'morale', label: 'Moral', value: '+8', tone: 'gain' }] },
    { id: 'conditioning-recover', title: 'Sesión de descarga', summary: 'Recuperas las piernas, pero el preparador registra una carga incompleta.', art: 'event-conditioning-recover', badge: 'Recuperación', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+14', tone: 'gain' }, { metric: 'reputation', label: 'Prestigio', value: '−4', tone: 'cost' }] },
  ]},
  { id: 'locker', title: 'Tensión en el vestuario', description: 'Una discusión divide al grupo antes del siguiente tramo.', icon: 'mdi:account-group-outline', riskBand: 'low', stages: [2, 3, 4], choices: [
    { id: 'locker-mediate', title: 'Mediar personalmente', summary: 'Inviertes energía en recomponer el grupo.', art: 'event-locker-mediate', badge: 'Vestuario', risk: 'medium', effects: [{ metric: 'physical', label: 'Físico', value: '−5', tone: 'cost' }, { metric: 'morale', label: 'Moral', value: '+12', tone: 'gain' }] },
    { id: 'locker-bracelet', title: 'Convocar una charla', summary: 'El Brazalete amplifica tu liderazgo, aunque sostener al grupo te desgasta.', art: 'event-locker-bracelet', badge: 'Reliquia requerida · Brazalete', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '−8', tone: 'cost' }, { metric: 'morale', label: 'Moral', value: '+18', tone: 'gain' }, { metric: 'persistent', label: 'Consumible', value: 'Charla del capitán', tone: 'gain' }] },
  ]},
  { id: 'physio', title: 'Laboratorio de fisioterapia', description: 'El cuerpo médico abre una ventana para probar un protocolo de recuperación.', icon: 'mdi:medical-bag', riskBand: 'low', stages: [3, 4], clubTiers: ['aspirant', 'elite'], choices: [
    { id: 'physio-treatment', title: 'Tratamiento convencional', summary: 'Descargas la musculatura, pero la sesión aislada pesa mentalmente.', art: 'event-physio-treatment', badge: 'Recuperación', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+22', tone: 'gain' }, { metric: 'morale', label: 'Moral', value: '−5', tone: 'cost' }] },
    { id: 'physio-camilla', title: 'Protocolo portátil', summary: 'La Camilla portátil acelera la recuperación mediante un protocolo exigente.', art: 'event-physio-camilla', badge: 'Reliquia requerida · Camilla portátil', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+35', tone: 'gain' }, { metric: 'morale', label: 'Moral', value: '−8', tone: 'cost' }, { metric: 'persistent', label: 'Consumible', value: 'Gel energético', tone: 'gain' }] },
  ]},
]

export interface EventChoiceChanges {
  physical?: number
  morale?: number
  ovr?: number
  reputation?: number
}

const EVENT_CHOICE_CHANGES: Readonly<Record<string, EventChoiceChanges>> = {
  'training-extra': { physical: -16, ovr: 1 },
  'training-rest': { physical: 10, morale: 4, reputation: -4 },
  'coach-accept': { physical: -4, morale: 8 },
  'injury-stop': { physical: 12, reputation: -5 },
  'press-speak': { physical: -5, morale: 10 },
  'press-focus': { physical: -3, morale: 6 },
  'academy-mentor': { physical: -6, morale: 14 },
  'academy-drill': { physical: -8, ovr: 1 },
  'conditioning-load': { physical: -10, ovr: 1 },
  'conditioning-biometric': { physical: -6, morale: 8 },
  'conditioning-recover': { physical: 14, reputation: -4 },
  'locker-mediate': { physical: -5, morale: 12 },
  'locker-bracelet': { physical: -8, morale: 18 },
  'physio-treatment': { physical: 22, morale: -5 },
  'physio-camilla': { physical: 35, morale: -8 },
}

export function getEventChoiceChanges(choiceId: string): EventChoiceChanges | undefined {
  const changes = EVENT_CHOICE_CHANGES[choiceId]
  return changes ? { ...changes } : undefined
}

const ACADEMY_NAMES: Readonly<Record<string, string>> = {
  España: 'cantera',
  Inglaterra: 'academy',
  Italia: 'settore giovanile',
  Alemania: 'Nachwuchs',
  Francia: 'centre de formation',
  Argentina: 'inferiores',
  Brasil: 'categorías de base',
  Uruguay: 'formativas',
  'Estados Unidos': 'academy',
  'Arabia Saudí': 'academia',
}

const TIER_PRESS: Readonly<Record<ClubTier, string>> = {
  second: 'la prensa local',
  modest: 'los medios de la ciudad',
  aspirant: 'la prensa nacional',
  elite: 'los focos internacionales',
}

export function getFootballEventContext(
  stage: number,
  clubName: string,
  leagueMovements: readonly LeagueMovement[] = [],
  nationalTeam?: string,
): FootballEventContext {
  const isInternationalStage = stage === 5 && Boolean(nationalTeam)
  return {
    stage,
    clubName,
    teamName: isInternationalStage ? nationalTeam! : clubName,
    clubTier: getClubTier(clubName, leagueMovements),
    country: isInternationalStage ? nationalTeam! : getEffectiveClub(clubName, leagueMovements)?.country ?? '',
  }
}

export function getEligibleEvents(context: FootballEventContext): readonly FootballEvent[] {
  return EVENTS.filter((event) => (
    (!event.stages || event.stages.includes(context.stage))
    && (!event.clubTiers || event.clubTiers.includes(context.clubTier))
  ))
}

function effectiveEventEffect(effect: DecisionEffect, stats: DecisionStatsSnapshot): DecisionEffect {
  const amountText = effect.value.replace('−', '-')
  if (!/^[+-]\d+$/.test(amountText)) return effect

  const stat = effect.metric === 'rating'
    ? { current: stats.ovr, maximum: 99 }
    : effect.metric === 'physical'
      ? { current: stats.physical, maximum: 100 }
      : effect.metric === 'morale'
        ? { current: stats.morale, maximum: 100 }
        : effect.metric === 'reputation'
          ? { current: stats.clubReputation, maximum: 100 }
          : null
  if (!stat) return effect

  const requested = Number(amountText)
  const next = Math.max(0, Math.min(stat.maximum, stat.current + requested))
  const effective = next - stat.current
  if (effective === 0) return { ...effect, value: 'Sin cambio', tone: 'neutral' }
  return { ...effect, value: `${effective > 0 ? '+' : '−'}${Math.abs(effective)}` }
}

export function getStateAwareEventChoices(
  event: FootballEvent,
  stats: DecisionStatsSnapshot,
): DecisionChoice[] {
  return event.choices.map((choice) => ({
    ...choice,
    effects: choice.effects.map((effect) => effectiveEventEffect(effect, stats)),
  }))
}

function withContextCopy(event: FootballEvent, context: FootballEventContext): FootballEvent {
  if (context.stage === 5) {
    if (event.id === 'coach') return {
      ...event,
      title: 'Conversación con el seleccionador',
      description: `El seleccionador de ${context.teamName} quiere saber si estás listo para asumir más responsabilidad en el Mundial.`,
    }
    if (event.id === 'training') return {
      ...event,
      title: 'Sesión opcional con la selección',
      description: `El cuerpo técnico de ${context.teamName} ofrece trabajo extra durante la concentración del Mundial.`,
    }
    if (event.id === 'injury') return {
      ...event,
      description: `Notas una sobrecarga en la concentración de ${context.teamName} antes del próximo cruce del Mundial.`,
    }
    if (event.id === 'conditioning') return {
      ...event,
      description: `Los preparadores de ${context.teamName} adaptan la sesión al calendario corto y exigente del Mundial.`,
    }
  }
  if (event.id === 'academy') {
    const academyName = ACADEMY_NAMES[context.country] ?? 'academia'
    return {
      ...event,
      title: `Visita a ${academyName}`,
      description: `Las jóvenes promesas de ${context.teamName} quieren compartir una sesión contigo.`,
      choices: event.choices.map((choice) => ({ ...choice, badge: academyName })),
    }
  }
  if (event.id === 'press') {
    return {
      ...event,
      description: `${TIER_PRESS[context.clubTier]} esperan una declaración de un jugador de ${context.teamName}. Hablar puede acercarte a la afición o distraerte del campo.`,
    }
  }
  if (event.id === 'coach' && context.stage === 1) {
    return {
      ...event,
      title: 'La puerta del primer equipo',
      description: `El entrenador de ${context.teamName} quiere saber si estás preparado para entrenar con los mayores.`,
      choices: event.choices.map((choice) => choice.id === 'coach-demand'
        ? { ...choice, title: 'Pedir una oportunidad', summary: 'Pides entrenar con el primer equipo y esperas su respuesta.' }
        : { ...choice, title: 'Seguir tu formación', summary: 'Aceptas el plan de la academia y conservas la confianza del cuerpo técnico.' }),
    }
  }
  return event
}

/**
 * Mantiene una cadencia intencional: un 35% de eventos volátiles y un 65% de
 * eventos de gestión. Cada evento conserva al menos una salida de riesgo bajo.
 */
export function pickContextualEvent(
  context: FootballEventContext,
  random: () => number = Math.random,
): FootballEvent {
  const eligible = getEligibleEvents(context)
  const preferredBand: EventRiskBand = random() < 0.35 ? 'high' : 'low'
  const preferred = eligible.filter((event) => event.riskBand === preferredBand)
  const pool = preferred.length ? preferred : eligible
  const picked = pool[Math.floor(random() * pool.length)] ?? EVENTS[0]!
  return withContextCopy(picked, context)
}
